// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 MindTenet LLC
// This file is part of Undef Engine.

import { useEffect, useRef, type RefObject } from 'react';

/**
 * Hook to trap focus within a container element
 * Prevents users from tabbing outside the container
 * Useful for modals and dialogs
 */
export function useFocusTrap(isActive: boolean = true): RefObject<HTMLDivElement | null> {
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!isActive || !containerRef.current) return;

    const container = containerRef.current;

    // Get all focusable elements within the container
    const getFocusableElements = () => {
      const selector =
        'a, button, textarea, input, select, [tabindex]:not([tabindex="-1"]), [contenteditable="true"]';
      return Array.from(container.querySelectorAll(selector)).filter(el => {
        const element = el as HTMLElement;
        return !element.hasAttribute('disabled') && element.offsetParent !== null;
      }) as HTMLElement[];
    };

    // Handle Tab key presses
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key !== 'Tab') return;

      const focusableElements = getFocusableElements();
      if (focusableElements.length === 0) return;

      const activeElement = document.activeElement as HTMLElement;
      const firstElement = focusableElements[0];
      const lastElement = focusableElements[focusableElements.length - 1];

      // If Shift+Tab on first element, focus last element
      if (e.shiftKey && activeElement === firstElement) {
        e.preventDefault();
        lastElement.focus();
      }
      // If Tab on last element, focus first element
      else if (!e.shiftKey && activeElement === lastElement) {
        e.preventDefault();
        firstElement.focus();
      }
    };

    container.addEventListener('keydown', handleKeyDown);

    // Focus first focusable element when trap is activated
    const focusableElements = getFocusableElements();
    if (focusableElements.length > 0) {
      focusableElements[0].focus();
    }

    return () => {
      container.removeEventListener('keydown', handleKeyDown);
    };
  }, [isActive]);

  return containerRef;
}
